import {
    AppSku,
    RideConditionContract,
    RideConditionPropertiesContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { GeoCoordinate } from '@polaris-customerweb/sharedcomponents.shared';
import { Feature, Point } from 'geojson';
import { cloneDeep } from 'lodash-es';
import { MapPoint } from './map-point';

export class RideCondition implements MapPoint {
    static GOOD_CONDITION = 66;
    static OK_CONDITION = 33;
    static POOR_CONDITION = 0;
    static TIME_TO_LIVE_SECONDS = 300;

    private _expiresTimestamp?: number;
    private _modificationDate?: Date;
    private _reportedTime?: Date;
    private rawRideCondition: RideConditionContract;

    constructor(rideConditionContract: RideConditionContract = {}) {
        this.rawRideCondition = cloneDeep(rideConditionContract);
    }

    get appSku(): AppSku {
        return this.properties().rideType ?? 'ORV';
    }

    get condition(): number {
        return this.properties().condition || 0;
    }

    get coordinates(): number[] {
        return (
            this.rawRideCondition.featureCollection?.features?.[0]?.geometry
                ?.coordinates ?? []
        );
    }

    get creationDate(): Date | undefined {
        if (!this.rawRideCondition.creationDate) {
            return undefined;
        }

        return new Date(this.rawRideCondition.creationDate);
    }

    get deletionDate(): Date | undefined {
        if (!this.rawRideCondition.deletionDate) {
            return undefined;
        }

        return new Date(this.rawRideCondition.deletionDate);
    }

    get description(): string {
        return this.properties().description || '';
    }

    set expiresTimestamp(timestamp: number) {
        this._expiresTimestamp = timestamp;
    }

    get geoCoordinate(): GeoCoordinate {
        return GeoCoordinate.fromCoordinateArray(this.coordinates);
    }

    get id(): string {
        return this.rawRideCondition.id || '';
    }

    get isExpired(): boolean {
        return (
            this._expiresTimestamp !== undefined &&
            this._expiresTimestamp < Date.now()
        );
    }

    get modificationDate(): Date {
        if (!this._modificationDate) {
            this._modificationDate = new Date(
                this.rawRideCondition.modificationDate as string
            );
        }

        return this._modificationDate;
    }

    get name(): string {
        const condition = this.properties().condition ?? 0;
        if (condition >= RideCondition.GOOD_CONDITION) {
            return 'Good';
        } else if (
            condition >= RideCondition.OK_CONDITION &&
            condition < RideCondition.GOOD_CONDITION
        ) {
            return 'Okay';
        } else if (
            condition >= RideCondition.POOR_CONDITION &&
            condition < RideCondition.OK_CONDITION
        ) {
            return 'Poor';
        } else {
            return '';
        }
    }

    get ownerEmail(): string {
        return this.rawRideCondition.ownerEmail || '';
    }

    get reportedByRider(): string {
        return this.properties().reportedByRider || '';
    }

    get reportedTime(): Date {
        if (!this._reportedTime) {
            this._reportedTime = new Date(
                this.properties().reportedTime as string
            );
        }

        return this._reportedTime;
    }

    get riderId(): string {
        return this.rawRideCondition.riderId || '';
    }

    clone(): RideCondition {
        return new RideCondition(this.toContract());
    }

    isWithinMaxAge(maxAgeInHours: number): boolean {
        const now = Date.now();
        const reportedTimestamp = this.reportedTime.getTime();
        const ageInHours = (now - reportedTimestamp) / 3600000;

        return ageInHours <= maxAgeInHours;
    }

    toContract(): RideConditionContract {
        return cloneDeep(this.rawRideCondition);
    }

    toGeoJson(): Feature<Point> {
        const contract = this.toContract();
        const geoJson = contract.featureCollection?.features?.[0];
        if (geoJson?.properties) {
            geoJson.properties.id = contract.id;
        }

        return geoJson as Feature<Point>;
    }

    private properties(): RideConditionPropertiesContract {
        return (
            this.rawRideCondition.featureCollection?.features?.[0].properties ??
            {}
        );
    }
}
